import cn from 'classnames';
import { FieldArray } from 'react-final-form-arrays';
import { useMemo, useRef, useState } from 'react';
import { Field } from 'react-final-form';

import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';
import { getFieldArrayErrorMessage } from 'CorePackage/MainModule/common/getFieldArrayErrorMessage';

import {
  IKnmErknmFormViolatedRequirementsMandatory,
  IKnmErknmFormViolatedRequirementsNpa,
} from '../../../KnmErknmPackage/MainModule/interfaces/IKnmErknmFormViolatedRequirementsNpa';
import { KnmCollapse } from '../../MainModule/components/KnmCollapse';
import styles from './KnmViolatedRequirementsNpaItem.module.scss';
import { Button } from '../../../UIPackage/MainModule/components/Button';
import { IRequirementsNpaInitValues } from '../../RequirementsNpaModalModule/components/RequirementsNpaModal';
import { IKnmFormRef } from '../../MainModule/interfaces/IKnmFormRef';
import { KnmViolatedRequirementsMandatoryAccordion } from './KnmViolatedRequirementsMandatoryAccordion';
import { checkHasErrorInCollapseBlock } from '../../../functions/checkHasErrorInCollapseBlock';
import { ShowMoreButton } from '../../../UIPackage/MainModule/components/ShowMoreButton/ShowMoreButton';
import { Spinner } from '../../../UIPackage/MainModule/components/Spinner';
import { useShowMore } from '../../../hooks/useShowMore';

interface IGroupedViolatedRequirementsNpaItemProps {
  groupNpa: IKnmErknmFormViolatedRequirementsNpa[];
  name: string;
  isViolationFieldsWatchOnly: boolean;
  watchOnly: boolean;
  onRemove: (npaIndex: number, npaId: number) => void;
  onRemoveStructuralUnit: (structuralUnitId: number) => void;
  addMandatory: (v: IRequirementsNpaInitValues, f: string) => void;
  formRef: IKnmFormRef | IDecisionFormRef | undefined;
  isMandatoryDisabled: boolean;
  isInsideDecisionsSection?: boolean;
  actRequirement?: IKnmErknmFormViolatedRequirementsNpa;
  id?: string;
  inAct?: boolean;
  isPaperless?: boolean;
  isKnm?: boolean;
  isPm?: boolean;
  isDecision?: boolean;
  onChangeFixPlanDate?: (date: string | Date) => void;
  allItems: IKnmErknmFormViolatedRequirementsNpa[];
  isDisabledByDirty?: boolean;
  isDisabledByRepeatCheck?: boolean;
}

export const GroupedViolatedRequirementsNpaItem = ({
  name,
  isViolationFieldsWatchOnly,
  watchOnly,
  onRemove,
  onRemoveStructuralUnit,
  addMandatory,
  formRef,
  isMandatoryDisabled,
  isInsideDecisionsSection,
  actRequirement,
  id,
  inAct,
  isPaperless,
  onChangeFixPlanDate,
  isKnm,
  isPm,
  isDecision,
  groupNpa,
  allItems,
  isDisabledByDirty = false,
  isDisabledByRepeatCheck,
}: IGroupedViolatedRequirementsNpaItemProps) => {
  const isErrorId = useRef<string>();
  const remove = formRef?.current.mutators.remove;

  const [isKnmCollapseOpened, setIsKnmCollapseOpened] = useState<boolean>(groupNpa[0].isNew as boolean);

  const handleAddMandatory = (item: IKnmErknmFormViolatedRequirementsNpa, mandatoryFieldName: string) => {
    addMandatory(
      {
        npa: item.npa,
        structuralUnit: item.structuralUnit,
      },
      mandatoryFieldName,
    );
  };

  const handleRemoveMandatory = (index: number, mandatoryFieldName: string) => {
    remove && remove(mandatoryFieldName, index);
  };

  const npaDocumentName = useMemo(() => {
    if (!groupNpa?.length) return '';

    if (groupNpa[0].npa.title?.toLocaleLowerCase().indexOf('приказ') !== -1) {
      return 'Приказ';
    }
    if (groupNpa[0].npa.title?.toLocaleLowerCase().indexOf('постановление') !== -1) {
      return 'Постановление';
    }
    if (groupNpa[0].npa.title?.toLocaleLowerCase().indexOf('указ') !== -1) {
      return 'Указ';
    }
    if (groupNpa[0].npa.title?.toLocaleLowerCase().indexOf('устав') !== -1) {
      return 'Устав';
    }
    if (groupNpa[0].npa.title?.toLocaleLowerCase().indexOf('кодекс') !== -1) {
      return 'Кодекс';
    }

    return 'Документ';
  }, [groupNpa]);

  const titleAccordions = useMemo(() => {
    return (
      <>
        {npaDocumentName} №{groupNpa[0].npa.number}
        <span className={styles.CollapseTitleGrey}> от {groupNpa[0].npa.acceptanceDate}</span>
      </>
    );
  }, [groupNpa]);

  const {
    visibleItems: visibleGroupItems,
    hasMore: hasMoreGroups,
    showMore,
    totalCount,
    visibleCount,
    isRendering,
  } = useShowMore(groupNpa, {
    initialCount: 3,
    incrementCount: 3,
  });

  const hasError = checkHasErrorInCollapseBlock(formRef, name);

  /**
   * Собственного id у группы быть не может: родитель раздаёт всем группам один и тот же
   * `id` (id акта), а якоря элементов - `${id}.violatedMandatoryRequirements[<guid>]`,
   * и группа покрывает произвольное подмножество элементов. Поэтому отдаём аккордеону
   * список id своих элементов - по ним он и раскроется по ссылке из панели ошибок.
   */
  const revealIds = useMemo(
    () =>
      groupNpa.map(item => {
        const indexInAllList = allItems.findIndex(
          i => i.npa.id === item.npa.id && i.structuralUnit.id === item.structuralUnit.id,
        );

        return `${id}.violatedMandatoryRequirements[${item.guid || indexInAllList}]`;
      }),
    [groupNpa, allItems, id],
  );

  return (
    <KnmCollapse
      revealIds={revealIds}
      theme="fields"
      title={titleAccordions}
      onClose={
        !watchOnly && !isDisabledByDirty && !isDisabledByRepeatCheck
          ? () => onRemove(-1, groupNpa[0].npa.id)
          : undefined
      }
      openByDefault={groupNpa[0].isNew}
      headerPostfix={isPaperless && groupNpa[0].isNew && <div className={styles.ActStatus}>Черновик</div>}
      changeOpenedCallback={setIsKnmCollapseOpened}
      error={hasError && isKnmCollapseOpened}
    >
      <>
        <div className={cn(styles.NpaName, { [styles.NpaNameNew]: isPaperless })}>{groupNpa[0].npa.title}</div>
        {visibleGroupItems.map(item => {
          const indexInAllList = allItems.findIndex(
            i => i.npa.id === item.npa.id && i.structuralUnit.id === item.structuralUnit.id,
          );
          const npaName = `${name}[${indexInAllList}]`;

          return (
            <Field key={npaName} name={npaName}>
              {({ meta }) => {
                const filedId = `${id}.violatedMandatoryRequirements[${item.guid || indexInAllList}]`;
                const mandatoryFieldName = `${npaName}.violatedMandatory`;
                const violatedMandatoryError = getFieldArrayErrorMessage(meta.error?.violatedMandatory) || '';

                if (meta.touched && violatedMandatoryError) {
                  if (!isErrorId.current) {
                    isErrorId.current = item.structuralUnit.id.toString();
                  }
                } else if (isErrorId.current && isErrorId.current === item.structuralUnit.id.toString()) {
                  isErrorId.current = '';
                }

                return (
                  <div id={filedId}>
                    <div className={cn(styles.StructureUnitTitle, styles.StructureUnitTitleNew)}>
                      <div>
                        {item.structuralUnit.title}
                        {!watchOnly && !isDisabledByDirty && !isDisabledByRepeatCheck && (
                          <button
                            className={styles.DeleteButton}
                            type="button"
                            onClick={() => onRemoveStructuralUnit(item.structuralUnit.id)}
                          >
                            <span className={styles.DeleteButtonIcon} />
                          </button>
                        )}
                      </div>
                    </div>
                    <div className={styles.BeforeMandatoryBlock} id={`${filedId}.violatedMandatory`}>
                      {getTitleFieldWithRedStar({
                        label: 'Нарушенные обязательные требования',
                        field: `${npaName}.violatedMandatory`,
                        isPm,
                        isKnm,
                        isDecision,
                        additionContent: !watchOnly && !isMandatoryDisabled && !isDisabledByRepeatCheck && (
                          <span className="shared-no-wrap-text">
                            <Button
                              className="shared-btn-add"
                              text="Добавить"
                              type="button"
                              variant="link"
                              color="primary"
                              disabled={isDisabledByDirty}
                              onClick={() => handleAddMandatory(item, mandatoryFieldName)}
                            />
                          </span>
                        ),
                      })}
                    </div>
                    {meta.touched && violatedMandatoryError && (
                      <p className="shared-input-error shared-row__small-margin">{violatedMandatoryError}</p>
                    )}
                    <FieldArray name={mandatoryFieldName}>
                      {({ fields }) =>
                        fields.map((violatedMandatoryName, index) => {
                          const mandatoryValue: IKnmErknmFormViolatedRequirementsMandatory = fields.value[index];
                          const correspondingActMandatoryViolations =
                            actRequirement?.violatedMandatory.find(
                              mandatory => mandatory.mandatory.id === mandatoryValue.mandatory.id,
                            )?.violations || [];
                          const mId = `${filedId}.violatedMandatory[${index}]`;

                          return (
                            <KnmViolatedRequirementsMandatoryAccordion
                              name={npaName}
                              formRef={formRef}
                              isMandatoryDisabled={isMandatoryDisabled}
                              isViolationFieldsWatchOnly={isViolationFieldsWatchOnly}
                              isDisabledByDirty={isDisabledByDirty}
                              watchOnly={watchOnly}
                              inAct={inAct}
                              isInsideDecisionsSection={isInsideDecisionsSection}
                              key={violatedMandatoryName}
                              violatedMandatoryName={violatedMandatoryName}
                              mandatoryValue={mandatoryValue}
                              index={index}
                              handleRemoveMandatory={ind => handleRemoveMandatory(ind, mandatoryFieldName)}
                              correspondingActMandatoryViolations={correspondingActMandatoryViolations}
                              isPaperless={true}
                              onChangeFixPlanDate={onChangeFixPlanDate}
                              mId={mId}
                              isKnm={isKnm}
                              isPm={isPm}
                              isDecision={isDecision}
                              touched={meta.touched}
                              isDisabledByRepeatCheck={isDisabledByRepeatCheck}
                              requirementNpa={actRequirement}
                            />
                          );
                        })
                      }
                    </FieldArray>
                  </div>
                );
              }}
            </Field>
          );
        })}
        {isRendering && <Spinner className={styles.Loader} />}
        {hasMoreGroups && !isRendering && (
          <ShowMoreButton onClick={showMore} totalCount={totalCount} visibleCount={visibleCount} />
        )}
      </>
    </KnmCollapse>
  );
};
